import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Edit, Delete, Add, Link as LinkIcon } from '@mui/icons-material';
import { authorsApi } from '../services/api';
import { Author, CreateAuthorDto, UpdateAuthorDto } from '../types';
import AuthorForm from '../components/AuthorForm';

type SortColumn = 'id' | 'name' | 'updatedAt' | 'sourceCount' | 'visibility';

const Authors: React.FC = () => {
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [selectedAuthor, setSelectedAuthor] = useState<Author | undefined>();
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; author?: Author }>({
    open: false,
  });
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await authorsApi.getAuthors({ pageSize: 100 });
      setAuthors(result.data);
    } catch (err: any) {
      console.error('Failed to fetch authors:', err);
      setError('Failed to load authors');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleCloseToast = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setToastOpen(false);
  };

  const handleCreateAuthor = () => {
    setSelectedAuthor(undefined);
    setFormOpen(true);
  };

  const handleEditAuthor = (author: Author) => {
    setSelectedAuthor(author);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedAuthor(undefined);
  };

  const handleFormSave = async (data: CreateAuthorDto | UpdateAuthorDto) => {
    try {
      if (selectedAuthor) {
        // Update existing author
        await authorsApi.updateAuthor(selectedAuthor.id, data as UpdateAuthorDto);
        setAuthors(prev =>
          prev.map(a =>
            a.id === selectedAuthor.id
              ? { ...a, ...data, updatedAt: new Date().toISOString() }
              : a
          )
        );
        showToast(`Author "${(data as UpdateAuthorDto).name}" updated successfully`, 'success');
      } else {
        // Create new author
        const newAuthor = await authorsApi.createAuthor(data as CreateAuthorDto);
        setAuthors(prev => [newAuthor, ...prev]);
        showToast(`Author "${(data as CreateAuthorDto).name}" created successfully`, 'success');
      }
    } catch (err: any) {
      console.error('Failed to save author:', err);
      showToast(
        err.response?.data?.message || 'Failed to save author',
        'error'
      );
      throw err;
    }
  };

  const handleDeleteClick = (author: Author) => {
    setDeleteDialog({ open: true, author });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.author) return;

    try {
      await authorsApi.deleteAuthor(deleteDialog.author.id);
      setAuthors(prev => prev.filter(a => a.id !== deleteDialog.author!.id));
      showToast(`Author "${deleteDialog.author.name}" deleted successfully`, 'success');
    } catch (err: any) {
      console.error('Failed to delete author:', err);
      showToast(
        err.response?.data?.message || 'Failed to delete author',
        'error'
      );
    } finally {
      setDeleteDialog({ open: false });
    }
  };

  const filteredAuthors = authors.filter(author =>
    author.name.toLowerCase().includes(search.toLowerCase()) ||
    author.bio?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSortClick = (column: SortColumn) => {
    if (sortColumn === column) {
      // If clicking the same column, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new column, set it with ascending order
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedAuthors = () => {
    let sorted = [...filteredAuthors];

    if (sortColumn) {
      sorted.sort((a, b) => {
        let aVal: any;
        let bVal: any;

        switch (sortColumn) {
          case 'id':
            aVal = a.id;
            bVal = b.id;
            break;
          case 'name':
            aVal = a.name.toLowerCase();
            bVal = b.name.toLowerCase();
            break;
          case 'updatedAt':
            aVal = new Date(a.updatedAt).getTime();
            bVal = new Date(b.updatedAt).getTime();
            break;
          case 'sourceCount':
            aVal = a.sourceCount;
            bVal = b.sourceCount;
            break;
          case 'visibility':
            aVal = a.isPublic ? 1 : 0;
            bVal = b.isPublic ? 1 : 0;
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return sorted;
  };

  const displayedAuthors = getSortedAuthors();

  const SortIndicator = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) {
      return null;
    }
    return (
      <span style={{ fontSize: '0.8em', marginLeft: '4px', fontWeight: 'normal' }}>
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Authors
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateAuthor}
          disabled={loading}
        >
          New Author
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by name or bio..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={loading}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredAuthors.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {authors.length === 0 ? 'No authors yet. Create one to get started!' : 'No authors match your search.'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell
                  onClick={() => handleSortClick('id')}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: '#eeeeee' },
                    fontWeight: 600,
                  }}
                >
                  ID <SortIndicator column="id" />
                </TableCell>
                <TableCell
                  onClick={() => handleSortClick('name')}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: '#eeeeee' },
                    fontWeight: 600,
                  }}
                >
                  Name <SortIndicator column="name" />
                </TableCell>
                <TableCell>Website</TableCell>
                <TableCell>Bio</TableCell>
                <TableCell
                  align="center"
                  onClick={() => handleSortClick('sourceCount')}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: '#eeeeee' },
                    fontWeight: 600,
                  }}
                >
                  Sources <SortIndicator column="sourceCount" />
                </TableCell>
                <TableCell
                  align="center"
                  onClick={() => handleSortClick('visibility')}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: '#eeeeee' },
                    fontWeight: 600,
                  }}
                >
                  Visibility <SortIndicator column="visibility" />
                </TableCell>
                <TableCell
                  onClick={() => handleSortClick('updatedAt')}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: '#eeeeee' },
                    fontWeight: 600,
                  }}
                >
                  Updated <SortIndicator column="updatedAt" />
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedAuthors.map((author) => (
                <TableRow key={author.id} hover>
                  <TableCell align="center" sx={{ fontSize: '0.9em', color: '#666' }}>
                    {author.id}
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {author.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {author.website ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <a
                          href={author.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration: 'none',
                            color: '#1976d2',
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                          }}
                          title={author.website}
                        >
                          {author.website}
                        </a>
                        <IconButton
                          size="small"
                          href={author.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open website"
                          sx={{ p: 0, ml: 0.5 }}
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <span style={{ color: '#999' }}>-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
                      {author.bio || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={author.sourceCount} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={author.isPublic ? 'Public' : 'Private'}
                      size="small"
                      color={author.isPublic ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.9em', color: '#666' }}>
                    {new Date(author.updatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditAuthor(author)}
                      title="Edit author"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(author)}
                      disabled={author.sourceCount > 0}
                      title={author.sourceCount > 0 ? 'Cannot delete: author has sources' : 'Delete author'}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AuthorForm
        open={formOpen}
        title={selectedAuthor ? 'Edit Author' : 'Create New Author'}
        author={selectedAuthor}
        onSave={handleFormSave}
        onClose={handleFormClose}
      />

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false })}
      >
        <DialogTitle>Delete Author</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the author "{deleteDialog.author?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false })}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toastSeverity}
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Authors;
